import { useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  TextField,
  Button,
  Typography,
  Alert,
} from '@mui/material';
import LightbulbIcon from '@mui/icons-material/Lightbulb';
import { useCreateFeature } from '@/hooks/useFeatures';
import { useToast } from '@/context/ToastContext';

interface FeatureFormProps {
  isLoggedIn: boolean;
  onLoginClick: () => void;
}

export default function FeatureForm({ isLoggedIn, onLoginClick }: FeatureFormProps) {
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const { showToast } = useToast();
  const createFeature = useCreateFeature();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!title.trim()) {
      showToast('请输入标题', 'error');
      return;
    }

    try {
      await createFeature.mutateAsync({
        title: title.trim(),
        description: description.trim() || undefined,
      });
      setTitle('');
      setDescription('');
      showToast('提案提交成功！', 'success');
    } catch {
      showToast('提交失败，请重试', 'error');
    }
  };

  if (!isLoggedIn) {
    return (
      <Card sx={{ mb: 3 }}>
        <CardContent sx={{ textAlign: 'center', py: 4 }}>
          <LightbulbIcon sx={{ fontSize: 48, color: 'warning.main', mb: 2 }} />
          <Typography variant="h6" gutterBottom>
            有好的功能建议？
          </Typography>
          <Button
            variant="contained"
            color="primary"
            onClick={onLoginClick}
            sx={{ mt: 1 }}
          >
            登录后提交提案
          </Button>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card sx={{ mb: 3 }}>
      <CardContent>
        <Typography variant="h6" gutterBottom>
          提交新提案
        </Typography>

        <Box component="form" onSubmit={handleSubmit} sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <TextField
            label="标题"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="简短描述你想要的功能"
            required
            fullWidth
            inputProps={{ maxLength: 100 }}
          />

          <TextField
            label="详细描述（可选）"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="详细说明这个功能的使用场景和预期效果"
            multiline
            rows={3}
            fullWidth
            inputProps={{ maxLength: 1000 }}
          />

          {createFeature.isError && (
            <Alert severity="error">提交失败，请重试</Alert>
          )}

          <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
            <Button
              type="submit"
              variant="contained"
              disabled={createFeature.isPending || !title.trim()}
            >
              {createFeature.isPending ? '提交中...' : '提交提案'}
            </Button>
          </Box>
        </Box>
      </CardContent>
    </Card>
  );
}
